import { fetchCatalogIds, getCatalogIdsForCategory } from '@/redux/modules/categoryCatalogCards';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { fetchItemsIfNeeded, getItem } from '@/redux/modules/item';
import {
    fetchSearchFacetsIfNeeded,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
} from '@/redux/modules/searchFacets';
import { fetchSimilarItemsV3IfNeeded, getSimilarItemIds } from '@/redux/modules/similarItemsV3';
import { getCategoryIdsFromPathname, getCategoryNameFromPathname, getFinalCategoryIdFromPathname } from '@/utils/urls';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery } from '@/utils/searchQuery';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/actions';
import { fetchAuctionNews } from '@/redux/modules/auctionNews';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalogAd';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog';
import { fetchCategoriesIfNeeded } from '@/redux/modules/category';
import { fetchCoverLotsIfNeeded } from '@/redux/modules/coverLots';
import { fetchItemBiddingIfNeeded } from '@/redux/modules/biddingInfo';
import { fetchSubcategories } from '@/redux/modules/subcategoryTiles';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getPromotedSearchResults } from '@/redux/modules/searchPromoted';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { isUserLoggedIn } from '@/redux/modules/user';
import { searchCategory } from '@/redux/modules/landingPage';
import { submitIsSavedSearchCheck } from '@/redux/modules/saveSearchCheck';

const DEFAULT_PRICE_GUIDE_SORT = '-saleStart';
export const DATA_LOADING_ID = 'category-results-plus-promoted';

export default ({ location, match, store }: DataLoaderParams) => {
    const { params } = match;
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageName = pathname.includes('price-guide') ? 'price-guide' : 'category';
    return Promise.all([
        dispatch(fetchSearchFacetsIfNeeded(archivedSearch)),
        dispatch(fetchCatalogAdIfNeeded({ categoryIds: getCategoryIdsFromPathname(pathname), pageName })),
        dispatch(fetchAuctionNews(getCategoryNameFromPathname(pathname))),
        dispatch(fetchSubcategories(getFinalCategoryIdFromPathname(pathname))),
        dispatch(fetchCatalogIds(getCategoryNameFromPathname(pathname))),
    ]).then(() => {
        const currentState = getState();
        const defaultSortOrder = archivedSearch ? DEFAULT_PRICE_GUIDE_SORT : DEFAULT_SORT_ORDER;
        const pageSize = getSearchPageSize(currentState);
        const catalogIds = getCatalogIdsForCategory(currentState);

        let rawFacets;
        if (archivedSearch) {
            rawFacets = getRawArchivedSearchFacets(currentState);
        } else {
            rawFacets = getRawUpcomingSearchFacets(currentState);
        }
        const searchQuery = buildSearchQuery({
            defaultSortOrder,
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
        });

        const userLoggedIn = isUserLoggedIn(currentState);
        let landingPageIds = []; // add more id's here
        if (params.categoryId2) {
            landingPageIds = [Number(params.categoryId), Number(params.categoryId2)];
        } else if (params.categoryId) {
            landingPageIds = [Number(params.categoryId)];
        }

        if (userLoggedIn) {
            const ssParams = getSaveSearchParams(queryParams, pathname);
            dispatch(submitIsSavedSearchCheck(ssParams));
        }

        return Promise.all([
            dispatch(searchCategory(searchQuery, landingPageIds)),
            dispatch(fetchCategoriesIfNeeded(landingPageIds)),
            dispatch(fetchCatalogsIfNeeded(catalogIds)),
            dispatch(fetchCoverLotsIfNeeded(catalogIds)),
        ]).then(() => {
            const searchState = getState();
            const itemIds = getSearchedItemIds(searchState);
            const promotedResults = getPromotedSearchResults(searchState);
            // make an array of requests to issue, dispatching analytics after they all complete
            // i have no idea why we care about `params.deleted` but apparently if it's
            // there we need to also fetch the item with its id
            if (itemIds?.length) {
                let itemPromises = [dispatch(fetchItemsIfNeeded([...itemIds, ...promotedResults], DATA_LOADING_ID))];
                if (params?.deleted && !isNaN(params.deleted)) {
                    itemPromises.push(dispatch(fetchItemsIfNeeded([params.deleted])));
                }
                return Promise.all(itemPromises);
            } else {
                const { soldItemIds = [] } = getSearchUiState(searchState);
                const promises = [
                    dispatch(fetchItemsIfNeeded(soldItemIds, DATA_LOADING_ID)),
                    dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
                ];

                soldItemIds.forEach((id) => promises.push(dispatch(fetchItemBiddingIfNeeded(id))));
                return Promise.all(promises).then(() => {
                    const state = getState();
                    const { itemId, sellerId, title } = getItem(state, soldItemIds[0]);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsV3IfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, itemId);
                        return dispatch(fetchItemsIfNeeded(similarItemIds));
                    });
                });
            }
        });
    });
};
